// Structured ↔ cron translation for the schedule form's "Simple" mode.
//
// Simple mode lets a non-technical user build the common 90% of schedules without cron syntax:
// hourly / daily / weekly / monthly, with a time-of-day, weekday selection, or day-of-month.
// These pure functions compose the structured fields INTO a 5-field cron expression, and
// decompose an existing cron BACK into the fields when editing. Anything that doesn't fit the
// structured shape (intervals, ranges, multiple times, "last day") returns null from decompose,
// and the form falls back to Advanced (raw cron) mode. Cron remains the escape hatch for everything.
//
// Cron is 5-field: "minute hour day-of-month month day-of-week" (weekday 0/7 = Sunday).

export type SimpleFrequency = "hourly" | "daily" | "weekly" | "monthly";

export interface SimpleSchedule {
  frequency: SimpleFrequency;
  /** Minute past the hour for Hourly (0–59). */
  minute: number;
  /** "HH:mm" local-to-timezone time for Daily/Weekly/Monthly. */
  time: string;
  /** Selected weekdays for Weekly, cron numbering 0–6 (0 = Sunday). At least one when frequency is weekly. */
  weekdays: number[];
  /** Day of month for Monthly, 1–28 (capped to avoid the 29–31 short-month trap). */
  dayOfMonth: number;
}

/** Weekday chips, Monday-first for display; value is the cron number (0 = Sunday). */
export const WEEKDAYS = [
  { value: 1, short: "Mon" },
  { value: 2, short: "Tue" },
  { value: 3, short: "Wed" },
  { value: 4, short: "Thu" },
  { value: 5, short: "Fri" },
  { value: 6, short: "Sat" },
  { value: 0, short: "Sun" },
] as const;

export const DEFAULT_SIMPLE: SimpleSchedule = {
  frequency: "daily",
  minute: 0,
  time: "09:00",
  weekdays: [1], // Monday
  dayOfMonth: 1,
};

function parseTime(time: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/** Compose the structured fields into a 5-field cron expression. */
export function toCron(s: SimpleSchedule): string {
  switch (s.frequency) {
    case "hourly": {
      const min = clampMinute(s.minute);
      return `${min} * * * *`;
    }
    case "daily": {
      const t = parseTime(s.time) ?? { hour: 9, minute: 0 };
      return `${t.minute} ${t.hour} * * *`;
    }
    case "weekly": {
      const t = parseTime(s.time) ?? { hour: 9, minute: 0 };
      const days = normaliseWeekdays(s.weekdays);
      return `${t.minute} ${t.hour} * * ${days.join(",")}`;
    }
    case "monthly": {
      const t = parseTime(s.time) ?? { hour: 9, minute: 0 };
      const dom = clampDayOfMonth(s.dayOfMonth);
      return `${t.minute} ${t.hour} ${dom} * *`;
    }
  }
}

/**
 * Decompose a cron expression back into the structured fields, or null if it can't be represented
 * in Simple mode (so the form falls back to Advanced/cron). Only the exact shapes toCron produces
 * round-trip: a single minute+hour, "*" where expected, weekday as a plain comma list, day-of-month
 * as a single 1–28 value. Anything with ranges, steps, names, or multiple hours fails fast.
 */
export function fromCron(cron: string): SimpleSchedule | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hr, dom, mon, dow] = parts;

  // Month must be unrestricted — Simple mode has no month selector.
  if (mon !== "*") return null;

  // Hourly: "<min> * * * *"
  if (hr === "*" && dom === "*" && dow === "*") {
    const minute = parseSingleInt(min, 0, 59);
    if (minute === null) return null;
    return { ...DEFAULT_SIMPLE, frequency: "hourly", minute };
  }

  // The remaining shapes all pin a single minute + hour.
  const minute = parseSingleInt(min, 0, 59);
  const hour = parseSingleInt(hr, 0, 23);
  if (minute === null || hour === null) return null;
  const time = `${pad(hour)}:${pad(minute)}`;

  // Daily: "<min> <hr> * * *"
  if (dom === "*" && dow === "*") {
    return { ...DEFAULT_SIMPLE, frequency: "daily", time };
  }

  // Weekly: "<min> <hr> * * <weekday list>"
  if (dom === "*" && dow !== "*") {
    const weekdays = parseWeekdayList(dow);
    if (weekdays === null) return null;
    return { ...DEFAULT_SIMPLE, frequency: "weekly", time, weekdays };
  }

  // Monthly: "<min> <hr> <day-of-month> * *"
  if (dow === "*" && dom !== "*") {
    const dayOfMonth = parseSingleInt(dom, 1, 28);
    if (dayOfMonth === null) return null;
    return { ...DEFAULT_SIMPLE, frequency: "monthly", time, dayOfMonth };
  }

  // Both day-of-month and day-of-week restricted → not a Simple shape.
  return null;
}

/** True if the cron can be edited in Simple mode (round-trips cleanly). */
export function isSimpleCron(cron: string): boolean {
  return fromCron(cron) !== null;
}

// ── internals ──────────────────────────────────────────────────────────────────────────────────

function parseSingleInt(field: string, lo: number, hi: number): number | null {
  if (!/^\d+$/.test(field)) return null; // rejects "*", ranges, steps, lists, names
  const n = Number(field);
  return n >= lo && n <= hi ? n : null;
}

/** Parse a plain comma list of weekday numbers (0–6), deduped and ordered. Sunday accepted as 0 only. */
function parseWeekdayList(field: string): number[] | null {
  const days = field.split(",").map((d) => parseSingleInt(d, 0, 6));
  if (days.some((d) => d === null)) return null;
  return normaliseWeekdays(days as number[]);
}

function normaliseWeekdays(days: number[]): number[] {
  const unique = [...new Set(days)].filter((d) => d >= 0 && d <= 6);
  if (unique.length === 0) return [1]; // never emit an empty weekday list
  return unique.sort((a, b) => a - b);
}

function clampMinute(min: number): number {
  return Math.min(59, Math.max(0, Math.round(min) || 0));
}

function clampDayOfMonth(dom: number): number {
  return Math.min(28, Math.max(1, Math.round(dom) || 1));
}
